import { z } from "zod";
// Reuse hằng độ dài subject dùng chung với admin-disputes (DRY — cùng cột Dispute.subject).
import {
  DISPUTE_SUBJECT_MIN_LENGTH,
  DISPUTE_SUBJECT_MAX_LENGTH,
} from "./admin-disputes.js";

/**
 * Shared DTO cho End-user Self-service (US-APP-IMP.F1-0141) — một nguồn sự thật api ↔ mobile (DRY).
 * 3 nhóm độc lập:
 *  1. password-change (@UC0142) — POST /v1/auth/password/change (authenticated).
 *  2. self-leave (@UC0056) — DELETE /v1/groups/:id/membership (member tự rời).
 *  3. create-dispute end-user (@UC0143) — POST /v1/disputes (báo tranh chấp trên 1 giao dịch).
 *
 * PO chốt 06/07: change-password KHÔNG revoke phiên khác; owner PHẢI transfer TRƯỚC khi rời
 * (OWNER_MUST_TRANSFER, giữ one-owner); dispute source=end_user, status=open, priority=medium,
 * auto-link ledger_transaction. `.strict()` chống over-post ở mọi request body.
 */

// ============================================================
// 1. password-change (@UC0142) — authenticated user đổi mật khẩu
// ============================================================

/** Chính sách độ mạnh mật khẩu mới cho end-user (khớp register/reset: ≥ 8 ký tự). */
export const ENDUSER_PASSWORD_MIN_LENGTH = 8;
/**
 * Chặn trên độ dài mật khẩu (review PR#234 LOW — DoS): argon2id trên chuỗi nhiều-MB rất tốn CPU
 * mỗi lần thử. 128 ký tự thừa cho passphrase mạnh, chặn khuếch đại DoS tại biên.
 */
export const ENDUSER_PASSWORD_MAX_LENGTH = 128;

/**
 * Body POST /v1/auth/password/change: `{ current, next }`.
 *  - current: mật khẩu hiện tại (verify — sai ⇒ 401 CURRENT_PASSWORD_INVALID). 1..max (chỉ so khớp).
 *  - next: mật khẩu mới, ENDUSER_PASSWORD_MIN_LENGTH..MAX (policy như register/reset + chặn DoS).
 *  - `.strict()` chặn field lạ tại biên (C5 — actor từ token, KHÔNG từ body).
 */
export const passwordChangeRequestSchema = z
  .object({
    current: z.string().min(1).max(ENDUSER_PASSWORD_MAX_LENGTH),
    next: z
      .string()
      .min(ENDUSER_PASSWORD_MIN_LENGTH)
      .max(ENDUSER_PASSWORD_MAX_LENGTH),
  })
  .strict();

export type PasswordChangeRequest = z.infer<typeof passwordChangeRequestSchema>;

/** Mã lỗi máy-đọc (RFC7807 `code`) cho password-change. */
export const PASSWORD_CHANGE_CODE = {
  /** current sai → 401. Client discriminate theo code, KHÔNG so khớp message. */
  CURRENT_PASSWORD_INVALID: "AUTH_CURRENT_PASSWORD_INVALID",
} as const;

export const PASSWORD_CHANGED_MESSAGE = "Đã đổi mật khẩu thành công.";
export const CURRENT_PASSWORD_INVALID_MESSAGE = "Mật khẩu hiện tại không đúng.";

// ============================================================
// 2. self-leave (@UC0056) — member tự rời nhóm
// ============================================================

/**
 * Response DELETE /v1/groups/:id/membership khi rời thành công: `{ status: "left" }`.
 * (Không cần request body — groupId từ path, actor từ token.)
 *
 * 🔴 US-E4.F2-0396: `"left"` ở đây mô tả **HÀNH ĐỘNG** ("bạn đã rời nhóm"), KHÔNG phải cột
 * `group_members.status` — cột đó nay là `join_pending` (hàng đợi, để O/A/T add lại được).
 * Cố ý KHÔNG đổi literal này thành `"join_pending"`: người vừa bấm "rời nhóm" mà app báo "đang
 * chờ duyệt" là sai hẳn nghĩa với họ. Hai tầng, hai từ vựng — đừng "đồng bộ cho khớp".
 */
export const selfLeaveResponseSchema = z
  .object({
    status: z.literal("left"),
  })
  .strict();

export type SelfLeaveResponse = z.infer<typeof selfLeaveResponseSchema>;

/** Mã lỗi self-leave — client discriminate theo code. */
export const SELF_LEAVE_CODE = {
  /** Còn vị thế tài chính (nợ/số dư) → chặn rời (reuse financial guard E2-22) → 409. */
  LEAVE_BLOCKED_FINANCIAL: "GROUP_LEAVE_BLOCKED_FINANCIAL",
  /** Owner PHẢI chuyển quyền TRƯỚC khi rời (giữ one-owner invariant) → 409. */
  OWNER_MUST_TRANSFER: "GROUP_OWNER_MUST_TRANSFER",
} as const;

export const LEAVE_BLOCKED_FINANCIAL_MESSAGE =
  "Bạn còn nghĩa vụ tài chính trong nhóm, không thể rời. Vui lòng tất toán trước.";
export const OWNER_MUST_TRANSFER_MESSAGE =
  "Bạn là chủ nhóm. Hãy chuyển quyền sở hữu cho thành viên khác trước khi rời nhóm.";

// ============================================================
// 3. create-dispute end-user (@UC0143) — báo tranh chấp trên 1 giao dịch
// ============================================================

/**
 * Body POST /v1/disputes: `{ subject, transactionId }`.
 *  - subject: mô tả ngắn vấn đề (user nhập; server KHÔNG suy diễn PII). DISPUTE_SUBJECT_MIN..MAX
 *    ký tự (trim), reuse hằng admin-disputes (4..300).
 *  - transactionId: UUID LedgerTransaction bị khiếu nại (auto-link entityType=ledger_transaction).
 *  - `.strict()` chặn field lạ (KHÔNG nhận status/priority/reporterUserId từ body — server quyết:
 *    status=open, priority=medium, reporterUserId=actor, source=end_user).
 */
export const createDisputeRequestSchema = z
  .object({
    subject: z
      .string()
      .trim()
      .min(DISPUTE_SUBJECT_MIN_LENGTH)
      .max(DISPUTE_SUBJECT_MAX_LENGTH),
    transactionId: z.string().uuid(),
  })
  .strict();

export type CreateDisputeRequest = z.infer<typeof createDisputeRequestSchema>;

/** Response tạo dispute (rút gọn — KHÔNG lộ nội bộ/tiền C2/G2): `{ code, status }`. */
export const createDisputeResponseSchema = z
  .object({
    code: z.string(),
    status: z.string(),
  })
  .strict();

export type CreateDisputeResponse = z.infer<typeof createDisputeResponseSchema>;
